import dayjs from 'dayjs';
import {
  PRODUCT_TYPE,
  ROUTES,
  USER_LIBRARY_TYPE,
} from '../../common/constants';
import {
  BROAD_EXPLORATION_CODE,
  ROADMAP_ERROR_MESSAGES,
  ROADMAP_SLOTS,
  ULTRA_PLAN_ROUTES,
} from './ultraPlanConstants';

export const getFullName = (person) =>
  [person?.firstName, person?.lastName]?.filter(Boolean)?.join(' ') || '-';

export const getSubscriberLabel = (user) => {
  const name = [user?.firstName, user?.lastName]?.filter(Boolean)?.join(' ');
  const userNumber = user?.userNumber ? `#${user?.userNumber}` : '';
  if (name && userNumber) return `${name} (${userNumber})`;
  return name || userNumber || '-';
};

export const getRoadmapWeeksCount = (roadmapSelections = []) =>
  new Set(roadmapSelections?.map((slot) => slot?.weekNumber))?.size || 0;

export const getActiveDefaultChild = (childProfiles = []) =>
  childProfiles?.find((child) => child?.isDefault && child?.isActive) || null;

export const getSlotConfig = (slotKey) =>
  ROADMAP_SLOTS?.find((slot) => slot?.key === slotKey);

export const getToyTypeSlot = (toy) =>
  ROADMAP_SLOTS?.find((slot) => slot?.toyType === toy?.type);

const getSlotEntry = (slot, position) => ({
  key: slot?.id || `empty-${position}`,
  position,
  slot,
  slotConfig: getSlotConfig(slot?.slotKey) || getToyTypeSlot(slot?.toy),
});

export const getWeekSlots = (week) => {
  const slots = [...(week?.slots || [])]?.sort(
    (a, b) => (a?.slotPosition ?? 0) - (b?.slotPosition ?? 0),
  );
  const lastPosition = slots?.[slots.length - 1]?.slotPosition || 0;
  const positionedSlots = Array.from(
    { length: lastPosition },
    (_, index) => index + 1,
  )?.flatMap((position) => {
    const matches = slots?.filter((slot) => slot?.slotPosition === position);
    return matches?.length
      ? matches?.map((slot) => getSlotEntry(slot, position))
      : [getSlotEntry(null, position)];
  });
  const unpositionedSlots = slots
    ?.filter((slot) => !slot?.slotPosition)
    ?.map((slot, index) => getSlotEntry(slot, lastPosition + index + 1));
  return [...positionedSlots, ...unpositionedSlots];
};

export const isBigToy = (toy) => toy?.type === PRODUCT_TYPE?.BIG_TOY?.value;

export const canWeekAddBigToy = ({ week, replacedSlotId, maxBigToys }) => {
  if (!Number.isInteger(maxBigToys)) return true;
  const bigToyCount = getWeekSlots(week)?.filter(
    ({ slot }) => slot?.id !== replacedSlotId && isBigToy(slot?.toy),
  )?.length;
  return bigToyCount < maxBigToys;
};

export const getBigToyLimitMessage = (maxBigToys, weekNumber) =>
  maxBigToys > 0
    ? `Week ${weekNumber} can have only ${maxBigToys} Big Toy${
        maxBigToys > 1 ? 's' : ''
      }`
    : 'Big Toys aren’t part of this plan';

export const isSlotOrdered = (slot) => !!slot?.isOrderPlaced;

export const getUnorderedWeekSlots = (week) =>
  getWeekSlots(week)?.filter(({ slot }) => slot?.toy && !isSlotOrdered(slot));

export const sortWeeks = (weeks = []) =>
  [...(weeks || [])]?.sort((a, b) => a?.weekNumber - b?.weekNumber);

export const getRoadmapErrorMessage = (error) => {
  const graphQLError = error?.graphQLErrors?.[0];
  return (
    ROADMAP_ERROR_MESSAGES?.[graphQLError?.extensions?.code] ||
    graphQLError?.message ||
    'Something went wrong while loading the roadmap.'
  );
};

export const getRoadmapRoute = ({ userId, userSubscriptionId }) =>
  ULTRA_PLAN_ROUTES?.ROADMAP?.replace(':userId', userId)?.replace(
    ':userSubscriptionId',
    userSubscriptionId,
  );

export const getSubscriberRoute = (params) =>
  `${getRoadmapRoute(params)}/subscriber`;

export const getInitials = (person) =>
  [person?.firstName, person?.lastName]
    ?.filter(Boolean)
    ?.map((name) => name?.[0]?.toUpperCase())
    ?.join('') || '?';

export const getChildAgeLabel = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const months = dayjs().diff(dayjs(dateOfBirth), 'month');
  if (months < 0) return null;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (!years) return `${remainingMonths} mo`;
  return remainingMonths ? `${years} yr ${remainingMonths} mo` : `${years} yr`;
};

const monthsToYears = (months) => Math.round(((months || 0) / 12) * 10) / 10;

export const getAgeGroupLabel = (ageGroup) => {
  if (!ageGroup || (!ageGroup?.minAge && !ageGroup?.maxAge)) return null;
  const maxYears = monthsToYears(ageGroup?.maxAge);
  return `${monthsToYears(ageGroup?.minAge)}–${maxYears} ${
    maxYears > 1 ? 'years' : 'year'
  }`;
};

const sortAgeGroups = (ageGroups = []) =>
  [...(ageGroups || [])]?.sort(
    (a, b) =>
      (a?.minAge ?? 0) - (b?.minAge ?? 0) ||
      (a?.maxAge ?? 0) - (b?.maxAge ?? 0),
  );

export const getToyAgeRanges = (toy) =>
  sortAgeGroups(toy?.ageGroups)
    ?.filter((ageGroup) => ageGroup?.minAge || ageGroup?.maxAge)
    ?.map(
      (ageGroup) =>
        `${monthsToYears(ageGroup?.minAge)}–${monthsToYears(ageGroup?.maxAge)}`,
    );

export const getToyAgeLabel = (toy) =>
  sortAgeGroups(toy?.ageGroups)
    ?.map(getAgeGroupLabel)
    ?.filter(Boolean)
    ?.join(', ') || null;

export const compareToysByAge = (a, b) => {
  const [ageA] = sortAgeGroups(a?.ageGroups);
  const [ageB] = sortAgeGroups(b?.ageGroups);
  if (!ageA || !ageB) return (ageA ? 0 : 1) - (ageB ? 0 : 1);
  return (
    (ageA?.minAge ?? 0) - (ageB?.minAge ?? 0) ||
    (ageA?.maxAge ?? 0) - (ageB?.maxAge ?? 0) ||
    (a?.name || '')?.localeCompare(b?.name || '')
  );
};

export const getPrimaryToyImage = (toy) =>
  [...(toy?.images || [])]?.sort(
    (a, b) => (a?.order ?? 0) - (b?.order ?? 0),
  )?.[0]?.url || null;

export const getWeekProgress = (week) => {
  const weekSlots = getWeekSlots(week);
  const assignedCount = weekSlots?.filter(({ slot }) => !!slot?.toy)?.length;
  return {
    assignedCount,
    totalCount: weekSlots?.length,
    emptyCount: weekSlots?.length - assignedCount,
    isComplete: weekSlots?.length > 0 && assignedCount === weekSlots?.length,
  };
};

export const getRoadmapSelectionStats = (roadmapSelections = []) => {
  const weeksCount = getRoadmapWeeksCount(roadmapSelections);
  return {
    weeksCount,
    assignedCount: roadmapSelections?.filter((slot) => !!slot?.toyId)?.length,
    totalSlots: [
      ...new Set(roadmapSelections?.map((slot) => slot?.weekNumber)),
    ]?.reduce(
      (total, weekNumber) =>
        total +
        getWeekSlots({
          slots: roadmapSelections?.filter(
            (slot) => slot?.weekNumber === weekNumber,
          ),
        })?.length,
      0,
    ),
  };
};

export const toggleAnswerCode = (question, codes, code) => {
  if (codes?.includes(code)) return codes?.filter((item) => item !== code);
  if (!question?.isMultiple || code === BROAD_EXPLORATION_CODE) return [code];
  return [...codes?.filter((item) => item !== BROAD_EXPLORATION_CODE), code];
};

export const getPreferenceCodes = (answers = {}) => {
  const codes = [...new Set(Object.values(answers || {})?.flat())];
  const specificCodes = codes?.filter(
    (code) => code !== BROAD_EXPLORATION_CODE,
  );
  return specificCodes?.length ? specificCodes : codes;
};

export const getUserLibrary = (user, type) =>
  user?.userLibraries?.find((userLibrary) => userLibrary?.type === type)
    ?.library;

export const getPrimaryLibrary = (user) =>
  getUserLibrary(user, USER_LIBRARY_TYPE?.PRIMARY?.value) ||
  user?.pincode?.library;

export const getAdditionalLibrary = (user) =>
  getUserLibrary(user, USER_LIBRARY_TYPE?.ADDITIONAL?.value);

export const getLibraryRoute = (library) =>
  `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${library?.id}/${library?.libraryNumber}`;
